import { Box, Paper, styled, useTheme } from "@mui/material";
import { gridClasses, GridColDef } from "@mui/x-data-grid";
import { GetFieldLookupItem, useFormMetadata, useLookups } from "@ngt/forms";
import { DateTime } from "luxon";
import { FC, useContext, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Attachment, FormDefinitions, Permissions } from "../api/dtos";
import StageAttachmentsContext from "../contexts/StageAttachmentsContext";
import usePermissions from "../hooks/auth/usePermissions";
import GridActions from "./GridActions";
import CollapsibleTablePremium from "./tables/CollapsibleTablePremium";
import { createCustomToolbarProps } from "./utility/CustomToolbarProps";
import ProjectContext from "../contexts/data/ProjectContext";
import StageContext from "../contexts/data/StageContext";
import { StyledIconButton } from "./StyledButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/pro-solid-svg-icons";

const StyledPaper = styled(Paper) (({theme}) => ({
    marginBottom: theme.spacing(2)
}));

const useAttachmentColumns = () => {
    const {data: permissions} = usePermissions();
    const metadata = useFormMetadata(FormDefinitions.Attachment);

    const {data: lookups, loading: _lookupsLoading} = useLookups(metadata);

    const params = useParams();

    const stageId = params?.stageId;
    const projectId = params?.projectId;

    const attachmentColumns = useMemo(() => {
        const columns: GridColDef<Attachment>[] = [
            {
                field: "file",
                headerName: "",
                width: 50,
                renderCell: (params) => (
                    params.row.file &&
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                height: "100%"
                            }}
                        >
                            <StyledIconButton
                                //@ts-expect-error
                                component="a"
                                href={`/forms/file/download/${params.row.file?.id}`}
                                size="small"
                            >
                                <FontAwesomeIcon icon={faDownload} />
                            </StyledIconButton>
                        </Box>
                )
            },
            {
                field: "name",
                headerName: "Name",
                flex: 1
            },
            {
                field: "fileName",
                headerName: "File Name",
                flex: 1,
                valueGetter: (_value, attachment) => attachment.file?.fileName
            },
            {
                field: "modifiedDate",
                headerName: "Date Modified",
                type: "date",
                valueFormatter: (_value, attachment) => {
                    return DateTime.fromISO(attachment.modifiedDate?.toString() ?? "").toFormat("dd/MM/yyyy");
                },
                flex: 1
            },
            {
                field: "attachmentTypeId",
                headerName: "Attachment Type",
                valueGetter: (_value, attachment) => GetFieldLookupItem(lookups, 'AttachmentTypeId', attachment.attachmentTypeId)?.value,
                flex: 1
            }
        ]

        if(permissions?.includes(Permissions.StageCanEditAttachment) || permissions?.includes(Permissions.StageCanDeleteAttachment)) {
            columns.push({
                field: "actions",
                headerName: '',
                renderCell: (params) => {
                    return (
                        <GridActions 
                            editPermission={Permissions.StageCanEditAttachment}
                            deletePermission={Permissions.StageCanDeleteAttachment}
                            editUrl={`/projects/${projectId}/stages/${stageId}/attachments/edit/${params.row.id}`}
                            deleteUrl={`/projects/${projectId}/stages/${stageId}/attachments/delete/${params.row.id}`}
                        />
                    )
                },
                filterable: false,
                sortable: false,
                disableExport: true
            });
        }

        return columns;
    }, [lookups])

    return attachmentColumns;
}

interface IStageAttachmentsProps {
    stageId: number;
}

const StageAttachments:FC<IStageAttachmentsProps> = () => {

    const {data: attachments, loading: attachmentsLoading} = useContext(StageAttachmentsContext);
    const {data: project} = useContext(ProjectContext);
    const {data: stage} = useContext(StageContext);

    const attachmentColumns = useAttachmentColumns();

    const customToolbarProps = useMemo(() => createCustomToolbarProps({
        exportTitle: `${project?.name} - ${stage?.name} - Attachments`,
        fileName: `${project?.name} - ${stage?.name} - Attachments`,
        includeDateTime: true
    }), [project, stage]);

    const theme = useTheme();
    return (
        <StyledPaper>
            <Box
                sx={{
                    marginTop: theme.spacing(2),
                    [`& .${gridClasses.root}`]: {
                        [`& .${gridClasses.columnHeader}`]: {
                            height: 'auto !important',
                            minHeight: 56,
                        
                            [`& .${gridClasses.columnHeaderTitleContainer}`]: {
                                whiteSpace: 'normal',
                        
                                [`& .${gridClasses.columnHeaderTitle}`]: {
                                    whiteSpace: 'normal',
                                    paddingTop: theme.spacing(1.5),
                                    paddingBottom: theme.spacing(1.5) 
                                }
                            },
                        
                            [`& .${gridClasses.iconButtonContainer}`]: {
                                width: 'unset',
                            },

                            '&.column-group-header': {
                                backgroundColor: theme.palette.grey[300],
                                boxShadow: "1",
                                textAlign: 'center'
                            }
                        }
                    },
                }}
            >
                <CollapsibleTablePremium
                    density="compact"
                    startCollapsed={false} 
                    title={"Attachments"} 
                    loading={attachmentsLoading} 
                    columns={attachmentColumns}
                    rows={attachments}
                    disableColumnMenu={false}
                    disableColumnFilter={false}
                    {...customToolbarProps}
                />
            </Box>
        </StyledPaper>
    );
}

export default StageAttachments;